import type { CoverageRule } from '@/shared/types';
import type { ScreeningResultsOption } from '../ScreeningResultsTypes';

export { calculateLeaseDurationMonths } from '@/shared/utils/leaseDurationMonths.util';

export const coverageMonthCountOptions = [3, 6, 12] as const;

/**
 * Calculates the gross total coverage based on months of coverage and rent details.
 * Subtracts prepaid rent and free rent from the total.
 */
export const calculateGrossTotalCoverage = (
  monthsOfCoverage?: number,
  grossMonthlyRent?: number,
  freeRent?: number,
  prepaidRent?: number,
): number =>
  Math.max(
    0,
    (monthsOfCoverage ?? 0) * (grossMonthlyRent ?? 0) - (prepaidRent ?? 0) - (freeRent ?? 0),
  );

/**
 * Calculates the number of months of coverage from total coverage and monthly rent.
 */
export const calculateMonthsOfCoverage = (
  grossMonthlyRent?: number,
  totalCoverage?: number,
): number => (grossMonthlyRent ? Math.max(0, (totalCoverage ?? 0) / grossMonthlyRent) : 0);

/**
 * Determines if a coverage option should be enabled based on lease duration.
 * - 3 months: enabled when lease >= 3 months
 * - 6 months: enabled when lease >= 6 months
 * - 12 months: enabled when lease >= 12 months
 */
export const isCoverageOptionEnabled = (
  coverageMonths: number,
  leaseDurationMonths: number,
): boolean => {
  if (coverageMonths === 3) return leaseDurationMonths >= 3;
  if (coverageMonths === 6) return leaseDurationMonths >= 6;
  if (coverageMonths === 12) return leaseDurationMonths >= 12;
  return false;
};

export const resolveInstitutionalCoverageOptions = (
  staticOptions: ScreeningResultsOption[],
  coverageRulesFromApi?: CoverageRule[],
): ScreeningResultsOption[] => {
  if (!coverageRulesFromApi?.length) {
    return staticOptions;
  }

  const isDisabled = (option: ScreeningResultsOption) =>
    !coverageRulesFromApi.some((rule) => rule.id === option.id);

  return staticOptions.map((option) => ({
    ...option,
    disabled: isDisabled(option),
    tooltip: isDisabled(option) ? option.disabledTooltip : option.tooltip,
  }));
};
